/**
 * MeetingRecorderWidget — TASK-673 (canvas-native capture).
 *
 * The in-person silent recorder as a summonable canvas tile, so starting and recording a
 * meeting stay ON the dashboard instead of navigating out to a full `/meeting/:id/live`
 * page. This is the epic's whole thesis (TASK-662): the meeting surface is the canvas.
 *
 * It self-fetches the session detail from `sessionId` (the only reliable identity a summon
 * carries) so the recorder gets the right client, type and — critically — `meeting_url`,
 * which gates the microphone off when a bot owns the audio. The recorder itself still gates
 * the mic behind explicit consent; nothing here starts capture on mount.
 */

import { useEffect, useRef } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Loader2 } from 'lucide-react';

import MeetingRecorder from '../../meeting/MeetingRecorder';
import { meetingApi } from '../../../services/api/meetingApi';
import { useCanvasActions } from '../../../stores/canvasStore';
import { WidgetContainer } from '../WidgetContainer';

interface MeetingRecorderWidgetProps {
  sessionId?: string;
  clientId?: string;
  clientName?: string;
}

export function MeetingRecorderWidget({
  sessionId,
  clientId,
  clientName,
}: MeetingRecorderWidgetProps) {
  const { addSpecs } = useCanvasActions();
  const recorderRegionRef = useRef<HTMLDivElement>(null);

  // The canvas scrolls newly appended tiles into view. Move keyboard focus as well so the
  // capture transition is explicit for sighted keyboard and screen-reader users. Defer until
  // the chooser unmounts: its dialog hook restores focus to the opener during cleanup.
  useEffect(() => {
    const focusTimer = window.setTimeout(
      () => recorderRegionRef.current?.focus({ preventScroll: true }),
      50,
    );
    return () => window.clearTimeout(focusTimer);
  }, []);

  const detailsQuery = useQuery({
    queryKey: ['meeting-details', sessionId],
    queryFn: () => meetingApi.getMeetingDetails(sessionId!),
    enabled: !!sessionId,
    staleTime: 60 * 1000,
  });
  const details = detailsQuery.data ?? null;
  const title = `Recording — ${clientName || details?.client_name || 'meeting'}`;

  if (!sessionId) {
    return (
      <WidgetContainer title="Recording">
        <p className="p-4 text-sm text-muted-foreground">No meeting selected to record.</p>
      </WidgetContainer>
    );
  }

  return (
    <div
      ref={recorderRegionRef}
      tabIndex={-1}
      role="region"
      aria-label={title}
      data-testid="meeting-recorder-region"
      className="outline-none focus-visible:ring-2 focus-visible:ring-kielis-blue"
    >
    <WidgetContainer title={title}>
      {detailsQuery.isLoading ? (
        <div role="status" aria-live="polite" className="flex items-center gap-2 p-6 text-sm text-muted-foreground">
          <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />
          Preparing the recorder…
        </div>
      ) : detailsQuery.isError ? (
        <div role="status" className="flex flex-wrap items-center gap-2 p-4 text-sm text-red-800">
          <span>This meeting could not be loaded.</span>
          <button type="button" onClick={() => detailsQuery.refetch()} className="font-medium underline hover:no-underline">
            Try again
          </button>
        </div>
      ) : (
        <MeetingRecorder
          embedded
          sessionId={sessionId}
          clientId={clientId ?? details?.client_id}
          clientName={clientName ?? details?.client_name}
          advisorId={details?.advisor_id}
          meetingType={details?.meeting_type}
          meetingUrl={details?.meeting_url}
          // TASK-673: after End, summon the review tile — the flow stays on the canvas.
          onEndMeeting={() =>
            addSpecs([
              {
                component: 'PostMeetingReviewWidget',
                props: { sessionId, clientName: clientName ?? details?.client_name },
              },
            ])
          }
        />
      )}
    </WidgetContainer>
    </div>
  );
}

export default MeetingRecorderWidget;
